import type { AvatarPose, EquipmentSlot } from "@nano/contracts";
import type { PoseSockets } from "./archetypes.ts";
import { COSMETIC_BY_ID } from "./cosmetics.ts";

/**
 * How each cosmetic is worn: which point of its sprite sits on which body
 * socket, and how big it is relative to that socket. Together with the
 * measured sockets in `archetypes.ts` this makes a hat sit ON the head,
 * glasses land on the eyes, a necklace wrap round the neck and a wand
 * end up in the hand, on every body and pose.
 *
 * Item coordinates are fractions of the item sprite (0..1 from top-left).
 */
export type WearMotion = "flap" | "cape" | "sway" | "float";

export interface ItemFit {
  /** Point of the sprite that lands on the socket (brim centre, crown base, lens midpoint, grip...). */
  readonly seat: { readonly x: number; readonly y: number };
  /** Fraction of the sprite width matched to `socket.width × scale` (inner brim, lens distance...). */
  readonly span: number;
  readonly scale: number;
  /** Pushes the seat down, in socket widths (a hat pulled over the hair). */
  readonly sink?: number;
  /** Extra clockwise tilt in degrees (a jaunty party hat). */
  readonly tilt?: number;
  /**
   * Sprite rows above this line are the far side of a loop (a necklace's
   * back, a scarf's ring) and go behind the body, so the item wraps round
   * the neck instead of lying over the chin.
   */
  readonly wrap?: number;
  /** Held props: tilt of the stick in the sprite, degrees clockwise from vertical. */
  readonly axis?: number;
  /** Held props: per-pose stick tilt overriding the hand socket's (a balloon trailing behind in profile). */
  readonly tiltIn?: Partial<Record<AvatarPose, number>>;
  /** Poses where the whole item goes behind the body (a tiara seen from behind). */
  readonly behindIn?: readonly AvatarPose[];
  /** Horizontal squash in the side pose (flat art seen edge-on). */
  readonly sideSquash?: number;
  /** In profile only the left half is drawn, seated at this x: the near lens of glasses, one wing. */
  readonly lens?: number;
  /** Temple arm colour drawn from the lens to the ear in profile. */
  readonly arm?: number;
  readonly motion?: WearMotion;
  /**
   * Fit of the item's own profile/back art (`assetIds.side`/`.back`), which
   * turns with the head instead of showing the front from every angle.
   */
  readonly views?: Partial<Record<AvatarPose, ViewFit>>;
}

export interface ViewFit {
  readonly seat: { readonly x: number; readonly y: number };
  readonly span: number;
  readonly scale: number;
  readonly sink?: number;
  readonly tilt?: number;
  /** Side pose: moves the seat toward the face, in socket widths (a tiara sits over the forehead). */
  readonly shift?: number;
}

/** Which body socket each slot is worn on. */
export const SLOT_SOCKET: Record<Exclude<EquipmentSlot, "aura">, keyof PoseSockets> = {
  head: "head",
  hair_accessory: "hair",
  face: "eyes",
  neck: "neck",
  back: "back",
  hand: "hand",
};

const CAPE_AND_WINGS: readonly AvatarPose[] = ["front", "side"];

export const ITEM_FIT: Readonly<Record<string, ItemFit>> = {
  // Head: hats are pulled down over the hair; crowns sit on the crown of the head.
  // Profile art faces right with its front (badge, centre gem) over the forehead.
  "hat-wizard": {
    seat: { x: 0.47, y: 0.8 },
    span: 0.95,
    scale: 1.12,
    sink: 0.28,
    views: {
      side: { seat: { x: 0.52, y: 0.9 }, span: 0.89, scale: 1.08, sink: 0.28, shift: 0.04 },
      back: { seat: { x: 0.5, y: 0.82 }, span: 0.97, scale: 1.12, sink: 0.28 },
    },
  },
  "hat-witch": {
    seat: { x: 0.45, y: 0.7 },
    span: 0.97,
    scale: 1.12,
    sink: 0.26,
    views: {
      side: { seat: { x: 0.5, y: 0.82 }, span: 0.9, scale: 1.08, sink: 0.26, shift: 0.04 },
      back: { seat: { x: 0.5, y: 0.62 }, span: 0.96, scale: 1.12, sink: 0.26 },
    },
  },
  "hat-party": {
    seat: { x: 0.5, y: 0.82 },
    span: 0.9,
    scale: 0.56,
    sink: 0.08,
    tilt: 12,
    views: {
      side: { seat: { x: 0.5, y: 0.85 }, span: 0.9, scale: 0.56, sink: 0.08, tilt: -10 },
      back: { seat: { x: 0.5, y: 0.8 }, span: 0.9, scale: 0.56, sink: 0.08, tilt: -12 },
    },
  },
  "crown-gold": {
    seat: { x: 0.5, y: 0.92 },
    span: 0.8,
    scale: 0.64,
    sink: 0.19,
    views: {
      side: { seat: { x: 0.5, y: 0.93 }, span: 0.9, scale: 0.64, sink: 0.19, shift: 0.03 },
      back: { seat: { x: 0.5, y: 0.92 }, span: 0.9, scale: 0.64, sink: 0.19 },
    },
  },
  "crown-star": {
    seat: { x: 0.5, y: 0.72 },
    span: 0.85,
    scale: 0.82,
    sink: 0.1,
    views: {
      // In profile the band runs from the forehead back to the ears: about half the head.
      side: { seat: { x: 0.5, y: 0.78 }, span: 0.93, scale: 0.6, sink: 0.12, shift: 0.18 },
      back: { seat: { x: 0.5, y: 0.62 }, span: 0.9, scale: 0.82, sink: 0.1 },
    },
  },
  "tiara-pink": {
    seat: { x: 0.5, y: 0.75 },
    span: 0.9,
    scale: 0.8,
    sink: 0.1,
    views: {
      side: { seat: { x: 0.5, y: 0.8 }, span: 0.87, scale: 0.58, sink: 0.12, shift: 0.18 },
      back: { seat: { x: 0.5, y: 0.72 }, span: 0.9, scale: 0.8, sink: 0.1 },
    },
  },
  "crown-roses": {
    seat: { x: 0.5, y: 0.55 },
    span: 0.95,
    scale: 1.02,
    sink: 0.2,
    views: {
      side: { seat: { x: 0.5, y: 0.6 }, span: 0.96, scale: 1.02, sink: 0.2 },
      back: { seat: { x: 0.5, y: 0.55 }, span: 0.95, scale: 1.02, sink: 0.2 },
    },
  },
  "bunny-ears": {
    seat: { x: 0.5, y: 0.6 },
    span: 0.7,
    scale: 0.95,
    sink: 0.08,
    views: {
      // Seen from the side the band is one arch over the ears and the ears overlap.
      side: { seat: { x: 0.55, y: 0.5 }, span: 1, scale: 0.47, sink: 0.04, shift: -0.12 },
      back: { seat: { x: 0.5, y: 0.6 }, span: 0.88, scale: 0.95, sink: 0.08 },
    },
  },
  "hat-sorceress": {
    seat: { x: 0.47, y: 0.78 },
    span: 0.95,
    scale: 1.12,
    sink: 0.27,
    views: {
      side: { seat: { x: 0.5, y: 0.88 }, span: 0.95, scale: 1.08, sink: 0.27, shift: 0.04 },
      back: { seat: { x: 0.5, y: 0.78 }, span: 0.98, scale: 1.12, sink: 0.27 },
    },
  },
  // The halo floats a little above the hair; the imp horns' band wraps over the crown of the head.
  "halo-gold": { seat: { x: 0.5, y: 0.5 }, span: 0.9, scale: 0.75, sink: -0.12 },
  "horns-imp": {
    seat: { x: 0.5, y: 0.35 },
    span: 0.8,
    scale: 1,
    sink: 0.15,
    views: {
      // In profile the band is one arch over the ears and the horns overlap.
      side: { seat: { x: 0.45, y: 0.36 }, span: 1, scale: 0.44, sink: 0.1, shift: -0.1 },
      back: { seat: { x: 0.5, y: 0.37 }, span: 0.88, scale: 1, sink: 0.15 },
    },
  },

  // Hair clips.
  "bow-pink": { seat: { x: 0.5, y: 0.4 }, span: 1, scale: 0.42 },
  "clip-star": { seat: { x: 0.35, y: 0.45 }, span: 1, scale: 0.45 },
  "clip-daisy": { seat: { x: 0.3, y: 0.5 }, span: 1, scale: 0.45 },
  "clip-butterfly": { seat: { x: 0.5, y: 0.5 }, span: 1, scale: 0.45 },

  // Face: lens centres on the pupils.
  "glasses-round": { seat: { x: 0.495, y: 0.45 }, span: 0.49, scale: 1, lens: 0.25, arm: 0xe0a030 },
  "glasses-heart": { seat: { x: 0.5, y: 0.45 }, span: 0.46, scale: 1, lens: 0.27, arm: 0xf06a8a },
  "glasses-star": { seat: { x: 0.5, y: 0.45 }, span: 0.42, scale: 1, lens: 0.29, arm: 0x7a4fc4 },
  "mask-masquerade": { seat: { x: 0.465, y: 0.6 }, span: 0.33, scale: 1, lens: 0.3, arm: 0xb27ad8 },

  // Neck: the loop's far side goes behind the neck.
  "necklace-pearl": { seat: { x: 0.5, y: 0.05 }, span: 0.5, scale: 1.25, wrap: 0.25 },
  "necklace-heart": { seat: { x: 0.5, y: 0.04 }, span: 0.66, scale: 1.3, wrap: 0.25 },
  "bowtie-red": { seat: { x: 0.5, y: 0.45 }, span: 1, scale: 1.25, sink: 0.25 },
  "scarf-striped": { seat: { x: 0.52, y: 0.2 }, span: 0.45, scale: 1.3, wrap: 0.22 },
  "scarf-rainbow": { seat: { x: 0.55, y: 0.15 }, span: 0.5, scale: 1.4, wrap: 0.2 },
  "medal-star": { seat: { x: 0.5, y: 0.05 }, span: 0.85, scale: 1.3, wrap: 0.3 },
  "lei-flower": { seat: { x: 0.5, y: 0.12 }, span: 0.8, scale: 1.6, wrap: 0.35 },
  "collar-bell": { seat: { x: 0.5, y: 0.2 }, span: 0.95, scale: 1.15, wrap: 0.25 },

  // Back: behind the body unless seen from behind.
  "cape-red": { seat: { x: 0.5, y: 0.1 }, span: 0.55, scale: 1.1, behindIn: CAPE_AND_WINGS, motion: "cape" },
  "cape-star": { seat: { x: 0.5, y: 0.15 }, span: 0.5, scale: 1.1, behindIn: CAPE_AND_WINGS, motion: "cape" },
  // In profile only the far-side wing is drawn, sweeping back from the shoulder blades.
  "wings-fairy": {
    seat: { x: 0.49, y: 0.6 },
    span: 1,
    scale: 2.7,
    sink: 0.1,
    lens: 0.49,
    sideSquash: 0.85,
    behindIn: CAPE_AND_WINGS,
    motion: "flap",
  },
  "wings-angel": {
    seat: { x: 0.5, y: 0.6 },
    span: 1,
    scale: 2.8,
    sink: 0.1,
    lens: 0.5,
    sideSquash: 0.85,
    behindIn: CAPE_AND_WINGS,
    motion: "flap",
  },

  // Hand: seat is the grip; `scale` is the prop width as a fraction of body height.
  "wand-heart": { seat: { x: 0.25, y: 0.72 }, span: 1, scale: 0.3, axis: 28, motion: "sway" },
  // Big props point further forward in profile so their heads clear the face.
  "staff-crystal": { seat: { x: 0.27, y: 0.68 }, span: 1, scale: 0.36, axis: 20, tiltIn: { side: 52 }, motion: "sway" },
  "lollipop-giant": { seat: { x: 0.22, y: 0.85 }, span: 1, scale: 0.34, axis: 22, tiltIn: { side: 64 }, motion: "sway" },
  "balloon-star": {
    seat: { x: 0.42, y: 0.97 },
    span: 1,
    scale: 0.32,
    axis: 0,
    tiltIn: { side: -22 },
    behindIn: ["side"],
    motion: "float",
  },
};

/**
 * Auras are live particle effects, not a picture behind the avatar. Each
 * particle is a frame cut from the aura's painted sprite (a heart, a
 * sparkle, a snowflake...), so the effect keeps the art style.
 */
export interface AuraFrame {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  /** Cut from another aura's art (its item id) instead of this one's. */
  readonly from?: string;
}

export type AuraMode =
  /** Pop in and out at random spots around the body, spinning a little. */
  | "twinkle"
  /** Drift upward from around the body, swaying, fading out. */
  | "rise"
  /** Fall from above the head to the feet, spinning slowly. */
  | "fall"
  /** Circle the waist, passing behind the body on the far side. */
  | "orbit";

export interface AuraEmitter {
  readonly mode: AuraMode;
  /** Indexes into `AuraFx.frames`. */
  readonly frames: readonly number[];
  readonly count: number;
  /** Particle size range, as fractions of body height. */
  readonly size: readonly [number, number];
  /** Lifetime (or orbit period) range in milliseconds. */
  readonly life: readonly [number, number];
}

/** How a particle is cut out of the painted aura (see particleArt.ts). */
export interface AuraCut {
  /** Alpha (0..255) a pixel needs to belong to the particle's solid shape. */
  readonly solid: number;
  /** Max RGB distance from the particle's core colour, where it touches other painted shapes. */
  readonly color?: number;
  /** Round particle: fade out the frame's corners, where neighbouring shapes reach in. */
  readonly round?: boolean;
}

export interface AuraFx {
  /** Particle frames, in pixels of the aura sprite. */
  readonly frames: readonly AuraFrame[];
  readonly cut: AuraCut;
  readonly emitters: readonly AuraEmitter[];
  /** Soft pulsing glow on the ground. */
  readonly glow: number;
}

const f = (x0: number, y0: number, x1: number, y1: number, from?: string): AuraFrame =>
  from ? { x: x0, y: y0, w: x1 - x0, h: y1 - y0, from } : { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };

export const AURA_FX: Readonly<Record<string, AuraFx>> = {
  "aura-sparkle": {
    cut: { solid: 200 },
    frames: [f(120, 13, 174, 74), f(239, 174, 298, 248), f(123, 238, 173, 290), f(243, 42, 268, 66), f(203, 65, 226, 89)],
    emitters: [
      { mode: "twinkle", frames: [0, 1, 2], count: 6, size: [0.1, 0.17], life: [900, 1500] },
      { mode: "rise", frames: [3, 4], count: 5, size: [0.05, 0.08], life: [1400, 2200] },
    ],
    glow: 0xffd45a,
  },
  "aura-hearts": {
    cut: { solid: 200, round: true },
    frames: [f(222, 30, 290, 90), f(6, 129, 58, 181), f(26, 187, 83, 241), f(187, 9, 222, 41), f(24, 69, 52, 93)],
    emitters: [
      { mode: "rise", frames: [0, 1, 2], count: 5, size: [0.12, 0.18], life: [1800, 2600] },
      { mode: "rise", frames: [3, 4], count: 4, size: [0.06, 0.09], life: [1500, 2200] },
    ],
    glow: 0xff7eb0,
  },
  "aura-snow": {
    cut: { solid: 170 },
    frames: [f(17, 31, 93, 119), f(225, 35, 285, 100), f(92, 12, 143, 70), f(42, 139, 77, 179), f(197, 6, 221, 31)],
    emitters: [
      { mode: "fall", frames: [0, 1, 2], count: 6, size: [0.09, 0.14], life: [2600, 3600] },
      { mode: "fall", frames: [3, 4], count: 6, size: [0.04, 0.07], life: [2200, 3000] },
    ],
    glow: 0x9fd4ff,
  },
  "aura-rainbow": {
    // Only the free-standing clouds cut cleanly off the band; its stars sit in the band's
    // glow, so the twinkles borrow the matching gold sparkles of the sparkle aura.
    cut: { solid: 200, color: 90, round: true },
    frames: [f(5, 117, 80, 177), f(238, 157, 313, 217), f(120, 13, 174, 74, "aura-sparkle"), f(243, 42, 268, 66, "aura-sparkle")],
    emitters: [
      { mode: "orbit", frames: [0, 2, 1, 3], count: 6, size: [0.1, 0.16], life: [5200, 5200] },
      { mode: "twinkle", frames: [2, 3], count: 3, size: [0.06, 0.1], life: [900, 1400] },
    ],
    glow: 0xc9a2ff,
  },
};

/** Art and cut settings for one particle frame of an aura (frames may borrow another aura's art). */
export function particleSource(auraId: string, frame: AuraFrame): { assetId: string; cut: AuraCut } {
  const id = frame.from ?? auraId;
  const fx = AURA_FX[id] ?? AURA_FX[auraId];
  return { assetId: COSMETIC_BY_ID.get(id)?.assetIds.front ?? `cosmetic.${id}`, cut: (fx ?? { cut: { solid: 200 } }).cut };
}
